import { supabase, updateSupabaseSessionToken } from "@/integrations/supabase/client";

interface EnsureSessionResult {
  success: boolean;
  tokenPresent: boolean;
  tokenValid: boolean;
  restored: boolean;
  error?: string;
}

/**
 * Ensures a valid hybrid session exists for the given user.
 * If the session token is missing or expired, creates a new one.
 * 
 * @param userId - The medewerker ID of the current user
 * @returns Result object with session status
 */
export const ensureHybridSession = async (userId: string): Promise<EnsureSessionResult> => {
  console.log("[ensureHybridSession] Checking session for user:", userId);
  
  const existingToken = localStorage.getItem('auth_session_token');
  const existingExpires = localStorage.getItem('auth_session_expires');
  
  // Check if token exists and is not expired
  if (existingToken && existingExpires) {
    const expiresDate = new Date(existingExpires);
    const now = new Date();
    
    if (now < expiresDate) {
      console.log("[ensureHybridSession] Valid token exists, expires:", existingExpires);
      return { 
        success: true, 
        tokenPresent: true, 
        tokenValid: true, 
        restored: false 
      };
    } else {
      console.log("[ensureHybridSession] Token expired at:", existingExpires);
    }
  } else {
    console.log("[ensureHybridSession] No token found in localStorage");
  }
  
  // Token missing or expired - create new session
  console.log("[ensureHybridSession] Creating new session for user:", userId);
  
  try {
    // Check if user is still active and registered before creating session
    const { data: medewerker, error: medewerkerError } = await supabase
      .from('medewerkers')
      .select('active, is_active, registered')
      .eq('id', userId)
      .single();

    if (medewerkerError || !medewerker?.active || !medewerker?.is_active || !medewerker?.registered) {
      console.log("[ensureHybridSession] User not active/registered, blocking session creation:", {
        active: medewerker?.active,
        is_active: medewerker?.is_active,
        registered: medewerker?.registered
      });
      localStorage.removeItem('auth_session_token');
      localStorage.removeItem('auth_session_expires');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('currentUser');
      return {
        success: false,
        tokenPresent: false,
        tokenValid: false,
        restored: false,
        error: 'Account niet actief of niet geregistreerd'
      };
    }

    // Enforce max 3 sessions per user
    const { data: activeSessions } = await supabase
      .from('auth_sessions')
      .select('id, created_at')
      .eq('medewerker_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (activeSessions && activeSessions.length >= 3) {
      const sessionsToDeactivate = activeSessions.slice(0, activeSessions.length - 2);
      for (const oldSession of sessionsToDeactivate) {
        await supabase
          .from('auth_sessions')
          .update({ is_active: false })
          .eq('id', oldSession.id);
      }
      console.log(`[ensureHybridSession] Deactivated ${sessionsToDeactivate.length} old sessions (limit: 3)`);
    }

    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 8);

    const sessionToken = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

    const { data: sessionData, error: sessionError } = await supabase.rpc('create_auth_session', {
      p_medewerker_id: userId,
      p_session_token: sessionToken,
      p_expires_at: expiresAt.toISOString(),
      p_user_agent: navigator.userAgent,
      p_ip_address: null,
    });
    
    if (sessionError) {
      console.error("[ensureHybridSession] Failed to create session:", sessionError);
      return {
        success: false,
        tokenPresent: false,
        tokenValid: false,
        restored: false,
        error: sessionError.message
      };
    }
    
    // Store in localStorage
    localStorage.setItem('auth_session_token', sessionData.session_token);
    localStorage.setItem('auth_session_expires', sessionData.expires_at);
    updateSupabaseSessionToken(sessionData.session_token);
    
    console.log("[ensureHybridSession] Session restored successfully:", sessionData?.id);
    
    return {
      success: true,
      tokenPresent: true,
      tokenValid: true,
      restored: true
    };
  } catch (error) {
    console.error("[ensureHybridSession] Unexpected error:", error);
    return {
      success: false,
      tokenPresent: false,
      tokenValid: false,
      restored: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Gets the current session debug info for display
 */
export const getSessionDebugInfo = () => {
  const token = localStorage.getItem('auth_session_token');
  const expires = localStorage.getItem('auth_session_expires');
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const currentUser = localStorage.getItem('currentUser');
  
  let user = null;
  try {
    user = currentUser ? JSON.parse(currentUser) : null;
  } catch (e) {
    // Ignore parse errors
  }
  
  const expiresDate = expires ? new Date(expires) : null;
  const now = new Date();
  const isExpired = expiresDate ? now > expiresDate : true;
  
  return {
    isAuthenticated,
    userId: user?.id || null,
    userName: user?.name || null,
    tokenPresent: !!token,
    tokenPreview: token ? `${token.slice(0, 8)}...${token.slice(-4)}` : null,
    expiresAt: expires,
    isExpired,
    expiresIn: expiresDate && !isExpired 
      ? Math.round((expiresDate.getTime() - now.getTime()) / 1000 / 60) + ' min'
      : null
  };
};
